import { useEffect, useRef } from 'react'
import { MessageSquare, Trash2 } from 'lucide-react'
import { useChat } from '../../hooks/useChat'
import ChatMessage from './ChatMessage'
import ChatInput from './ChatInput'
import LoadingSpinner from '../ui/LoadingSpinner'

const SUGGESTED_PROMPTS = [
  "What are today's NFL scores?",
  "Who's winning the NBA game right now?",
  "When do the Yankees play next?",
  "Show me the NFL standings",
]

export default function ChatInterface() {
  const { messages, sendMessage, clearChat, isLoading } = useChat()
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSuggestedPrompt = (prompt: string) => {
    sendMessage(prompt)
  }

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Sports Talk</h1>
            <p className="text-sm text-gray-500">Ask me anything about sports</p>
          </div>
        </div>
        {messages.length > 0 && (
          <button
            onClick={clearChat}
            className="btn-secondary flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Clear
          </button>
        )}
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto py-6">
        {messages.length === 0 ? (
          <EmptyState onPromptClick={handleSuggestedPrompt} />
        ) : (
          <div className="space-y-6">
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}
            {isLoading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                  <LoadingSpinner size="sm" />
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <LoadingSpinner size="sm" />
                    <span>Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input area */}
      <div className="pt-4 border-t border-gray-200">
        <ChatInput onSend={sendMessage} disabled={isLoading} />
      </div>
    </div>
  )
}

interface EmptyStateProps {
  onPromptClick: (prompt: string) => void
}

function EmptyState({ onPromptClick }: EmptyStateProps) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-4">
      <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-6">
        <MessageSquare className="w-8 h-8 text-primary-600" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        Welcome to Sports Talk
      </h2>
      <p className="text-gray-500 mb-8 max-w-md">
        Get real-time scores, schedules, standings, and analysis for NFL, NBA,
        MLB, NHL, and MLS.
      </p>
      <div className="w-full max-w-md">
        <p className="text-sm font-medium text-gray-700 mb-3">Try asking:</p>
        <div className="grid gap-2">
          {SUGGESTED_PROMPTS.map((prompt) => (
            <button
              key={prompt}
              onClick={() => onPromptClick(prompt)}
              className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-colors text-sm text-gray-700"
            >
              {prompt}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
